import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React Leaflet (only set once)
if (L.Icon.Default.prototype._getIconUrl) {
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  });
}

// Custom marker icon with brand colors and optional number
const createCustomIcon = (count = 1) => {
  const iconHtml = count > 1 
    ? `
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="50" viewBox="0 0 40 50">
        <path fill="#56cea4" stroke="#5722a8" stroke-width="2" d="M20 0C13.4 0 8 5.4 8 12c0 8 12 26 12 26s12-18 12-26c0-6.6-5.4-12-12-12z"/>
        <circle cx="20" cy="12" r="8" fill="#fff6d8"/>
        <text x="20" y="17" font-family="Arial" font-size="10" font-weight="bold" fill="#5722a8" text-anchor="middle">${count}</text>
      </svg>
    `
    : `
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42">
        <path fill="#56cea4" stroke="#5722a8" stroke-width="2" d="M16 0C9.4 0 4 5.4 4 12c0 8 12 26 12 26s12-18 12-26c0-6.6-5.4-12-12-12z"/>
        <circle cx="16" cy="12" r="6" fill="#fff6d8"/>
      </svg>
    `;

  return new L.DivIcon({
    html: iconHtml,
    iconSize: count > 1 ? [40, 50] : [32, 42],
    iconAnchor: count > 1 ? [20, 50] : [16, 42],
    popupAnchor: [0, count > 1 ? -50 : -42],
    className: 'custom-marker-icon'
  });
};

// Group entries by coordinates
const groupEntriesByLocation = (entries) => {
  const grouped = {};
  
  entries.forEach(entry => {
    if (entry.coordinates) {
      const key = `${entry.coordinates[0]},${entry.coordinates[1]}`;
      if (!grouped[key]) {
        grouped[key] = {
          coordinates: entry.coordinates,
          location: entry.location,
          entries: []
        };
      }
      grouped[key].entries.push(entry);
    }
  });
  
  return Object.values(grouped);
};

const GuestbookMap = ({ entries }) => {
  const mapKey = useRef(0);

  useEffect(() => {
    mapKey.current = Date.now();
  }, [entries.length]);

  const groupedLocations = groupEntriesByLocation(entries.filter(e => e.approved && e.coordinates));

  if (groupedLocations.length === 0) {
    return (
      <div className="map-container" style={{ height: '600px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#56cea4', fontSize: '1.5rem' }}>No hay mensajes en el mapa todavía...</p>
      </div>
    );
  }

  return (
    <div className="map-container">
      <MapContainer
        key={mapKey.current}
        center={[20, 0]}
        zoom={2}
        style={{ height: '600px', width: '100%', borderRadius: '12px', border: '4px solid #5722a8' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {groupedLocations.map((location, index) => (
          <Marker
            key={index}
            position={location.coordinates}
            icon={createCustomIcon(location.entries.length)}
          >
            <Popup className="custom-popup" maxWidth={500} minWidth={300}>
              <div className="popup-content-group">
                <div className="popup-header-group">
                  <h3>📍 {location.location}</h3>
                  {location.entries.length > 1 && (
                    <span className="popup-count">{location.entries.length} mensajes</span>
                  )}
                </div>
                
                <div className="popup-messages-list">
                  {location.entries.map((entry, idx) => (
                    <div key={entry.id} className="popup-message-item">
                      {location.entries.length > 1 && (
                        <div className="popup-message-number">{idx + 1}</div>
                      )}
                      <div className="popup-message-content">
                        <strong className="popup-author">{entry.name}</strong>
                        <p className="popup-message">{entry.message}</p>
                        {entry.image && (
                          <img 
                            src={`${process.env.REACT_APP_BACKEND_URL}${entry.image}`} 
                            alt="Memoria compartida" 
                            className="popup-image"
                          />
                        )}
                        <span className="popup-date">
                          {new Date(entry.timestamp).toLocaleDateString('es-ES')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default GuestbookMap;
